import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import pettyCashService from '../services/pettyCashService';
import s3Service from '../services/s3Service';
import styles from '../assets/pettyCashEdit.module.css';

const PettyCashEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [expense, setExpense] = useState({
    user_id: '',
    date: '',
    amount: '',
    description: '',
    file: null
  });
  const [employee, setEmployee] = useState('');
  const [errors, setErrors] = useState({});
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [showTypeahead, setShowTypeahead] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [existingFile, setExistingFile] = useState(null);
  const typeaheadRef = useRef(null);
  const datePickerRef = useRef(null);

  useEffect(() => {
    loadExpense();
    loadEmployees();
  }, [id]);

  // Click outside handler for typeahead dropdown and date picker
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showTypeahead && typeaheadRef.current && !typeaheadRef.current.contains(event.target)) {
        setShowTypeahead(false);
      }
      if (showDatePicker && datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setShowDatePicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTypeahead, showDatePicker]);

  const loadExpense = async () => {
    try {
      setLoading(true);
      const response = await pettyCashService.getExpense(id);
      if (response.success) {
        const expenseData = response.data;
        // Date is already in dd/mm/yyyy format from backend
        setExpense({
          user_id: expenseData.user_id || '',
          date: expenseData.date || '', // Already formatted as dd/mm/yyyy
          amount: expenseData.amount || '',
          description: expenseData.description || '',
          file: null,
          fileKey: expenseData.fileKey || null
        });
        setEmployee(expenseData.employee_name || '');
        setExistingFile(expenseData.file);
      } else {
        // Mock data for development
        setExpense({
          user_id: 1,
          date: '15/01/2024',
          amount: '100.00',
          description: 'Office supplies',
          file: null,
          fileKey: null
        });
        setEmployee('John Doe');
        setExistingFile('sample-file.pdf');
      }
    } catch (error) {
      console.error('Error loading expense:', error);
      // Mock data for development
      setExpense({
        user_id: 1,
        date: '15/01/2024',
        amount: '100.00',
        description: 'Office supplies',
        file: null,
        fileKey: null
      });
      setEmployee('John Doe');
      setExistingFile('sample-file.pdf');
    } finally {
      setLoading(false);
    }
  };

  const loadEmployees = async () => {
    try {
      const response = await pettyCashService.getEmployees();
      if (response.success) {
        setEmployees(response.data);
      } else {
        // Mock data for development
        setEmployees([
          { id: 1, name: 'John Doe', email: 'john@example.com' },
          { id: 2, name: 'Jane Smith', email: 'jane@example.com' },
          { id: 3, name: 'Mike Johnson', email: 'mike@example.com' },
          { id: 4, name: 'Sarah Wilson', email: 'sarah@example.com' },
          { id: 5, name: 'David Brown', email: 'david@example.com' }
        ]);
      }
    } catch (error) {
      console.error('Error loading employees:', error);
      // Mock data for development
      setEmployees([
        { id: 1, name: 'John Doe', email: 'john@example.com' },
        { id: 2, name: 'Jane Smith', email: 'jane@example.com' },
        { id: 3, name: 'Mike Johnson', email: 'mike@example.com' },
        { id: 4, name: 'Sarah Wilson', email: 'sarah@example.com' },
        { id: 5, name: 'David Brown', email: 'david@example.com' }
      ]);
    }
  };

  const handleInputChange = (field, value) => {
    setExpense(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }

    // Handle typeahead for employee search
    if (field === 'user_id') {
      if (value.length > 0) {
        const filtered = employees.filter(emp => 
          (emp.name && emp.name.toLowerCase().includes(value.toLowerCase())) ||
          (emp.email && emp.email.toLowerCase().includes(value.toLowerCase()))
        );
        setFilteredEmployees(filtered);
        setShowTypeahead(true);
      } else {
        setShowTypeahead(false);
      }
    }
  };

  const selectEmployee = (selectedEmployee) => {
    setExpense(prev => ({
      ...prev,
      user_id: selectedEmployee.id
    }));
    setEmployee(selectedEmployee.name);
    setShowTypeahead(false);
  };

  const handleDateClick = () => {
    setShowDatePicker(!showDatePicker);
  };

  const selectDate = (day, month, year) => {
    const formattedDate = `${day.toString().padStart(2, '0')}/${month.toString().padStart(2, '0')}/${year}`;
    setExpense(prev => ({
      ...prev,
      date: formattedDate
    }));
    setShowDatePicker(false);
  };

  const handleFileChange = async (file) => {
    try {
      // Upload file to S3 (matching Laravel path: public/petty_cash/files/)
      // useLaravelStructure: true ensures no timestamp prefix (matches Laravel's getDirectoryShort())
      const fileKey = await s3Service.uploadDocument(file, { 
        folder: 'public/petty_cash/files/',
        useLaravelStructure: true 
      });
      
      setExpense(prev => ({
        ...prev,
        file: file,
        fileKey: fileKey
      }));
      
      // Update existingFile to show the new file name
      // Extract filename from fileKey (e.g., "public/petty_cash/files/test.png" -> "test.png")
      const fileName = fileKey ? fileKey.split('/').pop() : file.name;
      setExistingFile(fileName);
      
      toast.success('File uploaded successfully');
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Failed to upload file');
    }
  };

  const handleRemoveFile = async () => {
    try {
      const response = await pettyCashService.removeFile(id, 'file');
      if (response.success) {
        setExistingFile(null);
        toast.success('File removed successfully');
      } else {
        toast.error('Failed to remove file');
      }
    } catch (error) {
      console.error('Error removing file:', error);
      toast.error('Failed to remove file');
    }
  };

  const handleDownloadFile = async () => {
    try {
      // Use backend API to download file (matches Laravel: route('petty-cash.getFile', [$expense->id, 'file']))
      // Backend redirects to presigned URL
      await pettyCashService.getFile(id, 'file');
      toast.success('File download started');
    } catch (error) {
      console.error('Error downloading file:', error);
      toast.error('Failed to download file');
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!expense.user_id) {
      newErrors.user_id = 'Employee name is required';
    }
    if (!expense.date) {
      newErrors.date = 'Date is required';
    } else {
      // Validate date format (dd/mm/yyyy)
      const dateRegex = /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/;
      if (!dateRegex.test(expense.date)) {
        newErrors.date = 'Date must be in dd/mm/yyyy format';
      }
    }
    if (!expense.amount) {
      newErrors.amount = 'Amount is required';
    } else if (isNaN(parseFloat(expense.amount))) {
      newErrors.amount = 'Amount must be a number';
    } else if (parseFloat(expense.amount) <= 0) {
      newErrors.amount = 'Amount must be greater than 0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix the errors below');
      return;
    }

    try {
      setSubmitting(true);
      
      const formData = new FormData();
      formData.append('user_id', expense.user_id);
      formData.append('date', expense.date);
      formData.append('amount', expense.amount);
      formData.append('description', expense.description || '');
      // Only send fileKey if file was uploaded to S3 (file is already uploaded, no need to send file object)
      if (expense.fileKey) {
        formData.append('fileKey', expense.fileKey);
      }
      // Note: We don't send expense.file because it's already uploaded to S3 via s3Service.uploadDocument()
      
      const response = await pettyCashService.updateExpense(id, formData);
      
      if (response.success) {
        toast.success(response.message || "Expense updated successfully!");
        navigate('/petty-cash', { state: { refresh: true } });
      } else {
        toast.error(response.message || "Failed to update expense");
      }
    } catch (error) {
      console.error('Error updating expense:', error);
      toast.error("Failed to update expense");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/petty-cash');
  };

  const generateCalendarDays = () => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth();
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
    const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay();
    
    const days = [];
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                       'July', 'August', 'September', 'October', 'November', 'December'];
    
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(null);
    }
    
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    
    return { 
      days, 
      monthName: monthNames[currentMonth], 
      year: currentYear 
    };
  };

  const { days, monthName, year } = generateCalendarDays();

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading...</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Breadcrumb Navigation */}
      <div className={styles.breadcrumbWrapper}>
        <ol className={styles.breadcrumb}>
          <li className={styles.breadcrumbItem}>
            <span onClick={() => navigate('/dashboard')} className={styles.breadcrumbLink} style={{ cursor: 'pointer', color: '#337ab7' }}>Dashboard</span>
          </li>
          <li className={styles.breadcrumbSeparator}>/</li>
          <li className={styles.breadcrumbItem}>
            <span onClick={() => navigate('/petty-cash')} className={styles.breadcrumbLink} style={{ cursor: 'pointer', color: '#337ab7' }}>Petty Cash</span>
          </li>
          <li className={styles.breadcrumbSeparator}>/</li>
          <li className={`${styles.breadcrumbItem} ${styles.breadcrumbActive}`}>Edit Petty Cash</li>
        </ol>
      </div>

      <div className={styles.row}>
        <div className={styles.colMd12}>
          <div className={`${styles.panel} ${styles.panelDefault} ${styles.panelForm}`}>
            <div className={styles.panelHeading}>
              <div className={styles.panelTitle}>
                <h3>Edit Petty Cash</h3>
              </div>
            </div>
            <div className={styles.panelBody}>
              <form onSubmit={handleSubmit} className={styles.pettyCashForm}>
                {/* Typeahead Container */}
                <div className={styles.jsPettycashTypeahead}></div>

                <fieldset className={styles.formHorizontal}>
                  {/* File Upload Section */}
                  <div className={styles.formGroup}>
                    <label className={`${styles.controlLabel} ${styles.colSm3}`}>
                      File
                    </label>
                    <div className={styles.colSm5}>
                      {/* Display existing file name */}
                      {existingFile && (
                        <div className={styles.existingFileInfo} style={{ marginBottom: '10px', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                          <strong>Current file:</strong> <span style={{ color: '#337ab7' }}>{existingFile}</span>
                        </div>
                      )}
                      <div className={`${styles.inputImage} ${existingFile ? styles.inputGroup : ''}`}>
                        <input
                          type="file"
                          className={styles.formControl}
                          style={{height: 'auto'}}
                          onChange={(e) => handleFileChange(e.target.files[0])}
                          accept=".pdf"
                        />
                        {existingFile && (
                          <div className={styles.inputGroupBtn}>
                            <button
                              type="button"
                              className={`${styles.btn} ${styles.btnDefault}`}
                              onClick={handleRemoveFile}
                              title="Remove file"
                            >
                              <i className="fa fa-fw fa-trash"></i>Delete
                            </button>
                          </div>
                        )}
                      </div>
                      <div className={styles.helpText}>
                        The file can not be larger than 100MB. If you don't want to change file - just leave the field empty.
                      </div>
                    </div>
                  </div>

                  {/* Download existing file */}
                  {existingFile && (
                    <div className={`${styles.row} ${styles.mb30}`}>
                      <div className={styles.colSmPush3}>
                        <button
                          type="button"
                          className={`${styles.btn} ${styles.btnWarning} ${styles.hiddenPrintLink}`}
                          onClick={handleDownloadFile}
                        >
                          Download
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Employee Name */}
                  <div className={styles.formGroup}>
                    <label className={`${styles.controlLabel} ${styles.colSm3} ${styles.labelRequired}`}>
                      Employee Name
                    </label>
                    <div className={styles.colSm5}>
                      <div className={styles.typeaheadContainer} ref={typeaheadRef}>
                        <input
                          type="text"
                          className={`${styles.formControl} ${styles.typeahead}`}
                          value={employee}
                          onChange={(e) => handleInputChange('user_id', e.target.value)}
                          placeholder="Search employee..."
                        />
                        {showTypeahead && filteredEmployees.length > 0 && (
                          <div className={styles.typeaheadDropdown}>
                            {filteredEmployees.map((emp) => (
                              <div
                                key={emp.id}
                                className={styles.typeaheadItem}
                                onClick={() => selectEmployee(emp)}
                              >
                                {emp.name} ({emp.email})
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      {errors.user_id && (
                        <span className={styles.helpBlock}>{errors.user_id}</span>
                      )}
                    </div>
                  </div>

                  {/* Date */}
                  <div className={styles.formGroup}>
                    <label className={`${styles.controlLabel} ${styles.colSm3} ${styles.labelRequired}`}>
                      Date
                    </label>
                    <div className={styles.colSm2}>
                      <div className={styles.datePickerContainer} ref={datePickerRef}>
                        <input
                          type="text"
                          className={`${styles.formControl} ${styles.jsDatepicker}`}
                          value={expense.date}
                          onChange={(e) => handleInputChange('date', e.target.value)}
                          onClick={handleDateClick}
                          placeholder="dd/mm/yyyy"
                          readOnly
                          required
                        />
                        {showDatePicker && (
                          <div className={styles.datePickerDropdown}>
                            <div className={styles.datePickerHeader}>
                              <span>{monthName} {year}</span>
                            </div>
                            <div className={styles.datePickerGrid}>
                              <div className={styles.datePickerWeekdays}>
                                <span>Sun</span>
                                <span>Mon</span>
                                <span>Tue</span>
                                <span>Wed</span>
                                <span>Thu</span>
                                <span>Fri</span>
                                <span>Sat</span>
                              </div>
                              <div className={styles.datePickerDays}>
                                {days.map((day, dayIndex) => (
                                  <span
                                    key={dayIndex}
                                    className={`${styles.datePickerDay} ${day ? styles.available : styles.empty}`}
                                    onClick={() => day && selectDate(day, new Date().getMonth() + 1, year)}
                                  >
                                    {day}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      {errors.date && (
                        <span className={styles.helpBlock}>{errors.date}</span>
                      )}
                    </div>
                  </div>

                  {/* Amount */}
                  <div className={styles.formGroup}>
                    <label className={`${styles.controlLabel} ${styles.colSm3} ${styles.labelRequired}`}>
                      Amount
                    </label>
                    <div className={styles.colSm5}>
                      <input
                        type="text"
                        className={styles.formControl}
                        value={expense.amount}
                        onChange={(e) => handleInputChange('amount', e.target.value)}
                        placeholder="0.00"
                        required
                      />
                      {errors.amount && (
                        <span className={styles.helpBlock}>{errors.amount}</span>
                      )}
                    </div>
                  </div>

                  {/* Description */}
                  <div className={styles.formGroup}>
                    <label className={`${styles.controlLabel} ${styles.colSm3}`}>
                      Description
                    </label>
                    <div className={styles.colSm5}>
                      <textarea
                        className={styles.formControl}
                        value={expense.description}
                        onChange={(e) => handleInputChange('description', e.target.value)}
                        placeholder="Enter description..."
                        rows="3"
                      />
                      {errors.description && (
                        <span className={styles.helpBlock}>{errors.description}</span>
                      )}
                    </div>
                  </div>

                </fieldset>

                <div className={`${styles.formGroup} ${styles.textRight}`} style={{display: 'flex', justifyContent: 'flex-end'}}>
                  <button
                    type="submit"
                    className={`${styles.btn} ${styles.btnHead} ${styles.btnStandard}`}
                    disabled={submitting}
                  >
                    {submitting ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PettyCashEdit; 